import React, { useEffect, useState } from 'react';
import {
  Button,
  ChevronLeftIcon,
  Label,
  NumberInput,
  TextInput,
} from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { createSpareCategory, updateSpareCategory } from '../../../services/mutations';
import type { SpareCategory, SpareCategoryTreeNode } from '../spare-categories/useData';

type Props = {
  libraryId: number;
  parentId: number | null;
  parentLabel?: string | null;
  editItem: SpareCategoryTreeNode | SpareCategory | null;
  onCancel: () => void;
  onSaved: (saved: SpareCategory) => void;
};

type FormState = {
  name: string;
  abrv: string;
  sortOrder: string;
  isActive: boolean;
};

const emptyForm = (): FormState => ({
  name: '',
  abrv: '',
  sortOrder: '0',
  isActive: true,
});

/** Inline right-panel form for folder / sub-folder (category) create & edit. */
export const SpareCategoryFormPanel: React.FC<Props> = ({
  libraryId,
  parentId,
  parentLabel,
  editItem,
  onCancel,
  onSaved,
}) => {
  const { colors } = useTheme();
  const isEdit = editItem != null;
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (editItem) {
      setForm({
        name: editItem.name ?? '',
        abrv: editItem.abrv ?? '',
        sortOrder: String(editItem.sortOrder ?? 0),
        isActive: editItem.isActive ?? true,
      });
    } else {
      setForm(emptyForm());
    }
    setError('');
  }, [editItem, parentId, libraryId]);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = form.name.trim();
    if (!name) {
      setError('Name is required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const sortOrder = Number(form.sortOrder);
      const payload = {
        libraryId,
        pid: isEdit ? (editItem!.pid ?? null) : parentId,
        name,
        abrv: form.abrv.trim() || null,
        sortOrder: Number.isFinite(sortOrder) ? sortOrder : 0,
        isActive: form.isActive,
      };
      const saved = isEdit
        ? await updateSpareCategory(editItem!.id, payload)
        : await createSpareCategory(payload);
      onSaved(saved as SpareCategory);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setError(e?.response?.data?.message || e?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const title = isEdit
    ? parentId != null || editItem?.pid
      ? 'Edit sub-folder'
      : 'Edit folder'
    : parentId != null
      ? 'New sub-folder'
      : 'New folder';

  return (
    <div className="flex flex-col h-full min-h-0">
      <div
        className={cn(
          'flex-shrink-0 flex items-center gap-2 px-4 py-2.5 border-b',
          colors.border,
          colors.surfaceHover,
        )}
      >
        <button
          type="button"
          onClick={onCancel}
          className={cn(
            'inline-flex items-center justify-center h-8 w-8 rounded-md',
            colors.textMuted,
            colors.interactiveHover,
          )}
          title="Back"
          aria-label="Back"
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </button>
        <div className="min-w-0">
          <h2 className={cn('text-sm font-semibold truncate', colors.text)}>{title}</h2>
          <p className={cn('text-xs truncate', colors.textMuted)}>
            {parentId != null
              ? `Under: ${parentLabel || `Category #${parentId}`}`
              : isEdit && editItem?.pid
                ? 'Sub-folder'
                : 'Top-level folder'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex-1 min-h-0 overflow-y-auto dashboard-scrollbar p-4 space-y-4">
        <section className={cn('rounded-xl border p-4 space-y-4', colors.border, colors.surface)}>
          <div>
            <Label htmlFor="cat-name">Name</Label>
            <TextInput
              id="cat-name"
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              placeholder="Folder name"
              className="mt-1.5"
              autoFocus
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="cat-abrv">Abbreviation</Label>
              <TextInput
                id="cat-abrv"
                value={form.abrv}
                onChange={(e) => setField('abrv', e.target.value)}
                placeholder="Optional"
                className="mt-1.5"
                maxLength={10}
              />
            </div>
            <div>
              <Label htmlFor="cat-sort">Sort order</Label>
              <NumberInput
                id="cat-sort"
                value={form.sortOrder}
                onChange={(e) => setField('sortOrder', String(e.target.value ?? '0'))}
                className="mt-1.5"
              />
            </div>
          </div>
          <label
            className={cn(
              'flex items-start gap-3 rounded-lg px-3 py-2.5 cursor-pointer select-none',
              colors.surfaceHover,
            )}
          >
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setField('isActive', e.target.checked)}
              className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <div className="min-w-0">
              <p className={cn('text-sm font-medium', colors.text)}>Active</p>
              <p className={cn('text-xs mt-0.5', colors.textMuted)}>Inactive folders stay in the tree but are dimmed.</p>
            </div>
          </label>
        </section>

        {error ? <p className="text-sm text-red-500">{error}</p> : null}

        <div className="flex items-center justify-end gap-2 pb-2">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save' : 'Create'}
          </Button>
        </div>
      </form>
    </div>
  );
};
